import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { AppContext } from "../../App";
import { Button, CardWithTable, Pagination } from "@activehub/common-components-spa-resource";
import DownloadOrEmailCodesModal from "./actionmodal/DownloadOrEmailCodesModal";
import { Loader } from '../../components/loader/Loader';
import "./ManageAccessCodeStepTwo.scss";

const ManageAccessCodeStepTwo = () => {
    const [accessCodeModalAction, setAccessCodeModalAction] = useState("");
    const [perPageDataCount, setPerPageDataCount] = useState(0);
    const [activePageNum, setActivePageNum] = useState(0);
    const [currentRequestId, setCurrentRequestId] = useState("");
    const [accessCodeOrderStatus, setAccessCodeOrderStatus] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);
    const [disableActionButtons, setDisableActionButtons] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [apiError, setApiError] = useState("");
    const [paginationResetKey, setPaginationResetKey] = useState(0);

    const {
        translateHTML: tHTML,
        manageAccessCodeDetails,
        isLoadingAccessCodes,
        setManageCodesError,
        getAccessCodeDetails,
        currentIsbn,
        setManageAccessCodeDetails
    } = useContext(AppContext);

    const tableRef = useRef(null);
    const [pageSize, setPageSize] = useState(30);

    const safeAccessCodeDetails = Array.isArray(manageAccessCodeDetails?.content)
        ? manageAccessCodeDetails.content
        : [];

    const totalElements = manageAccessCodeDetails?.totalElements || 0;
    const startEntry = totalElements > 0 ? currentPage * pageSize + 1 : 0;
    const endEntry = totalElements > 0 ? Math.min((currentPage + 1) * pageSize, totalElements) : 0;
    const displayPageSize = pageSize === totalElements ? "All" : pageSize;

    const paginationData = {
        totalElements: totalElements,
        pageCount: manageAccessCodeDetails?.totalPages || 0,
        currentPageIndex: currentPage,
        pageSize: displayPageSize,
        numberOfElements: manageAccessCodeDetails?.numberOfElements || 0,
        hasNextPage: currentPage < (manageAccessCodeDetails?.totalPages || 0) - 1,
        hasPreviousPage: currentPage > 0,
        nextPage: () => {
            const nextPageIndex = currentPage + 1;
            if (nextPageIndex < (manageAccessCodeDetails?.totalPages || 0)) {
                onPageChange(nextPageIndex);
            }
        },
        previousPage: () => {
            const prevPageIndex = currentPage - 1;
            if (prevPageIndex >= 0) {
                onPageChange(prevPageIndex);
            }
        },
        setPage: (pageIndex) => {
            onPageChange(pageIndex);
        },
        setPageSize: (newPageSize) => {
            handlePageSizeChange(newPageSize);
        }
    };

    const handleShowActionModal = (actionType) => {
        if (disableActionButtons) return;
        setAccessCodeModalAction(actionType);
    };

    const handleSelectedItemsChange = (items) => {
        let singleSelectionItems = [];
        if (items && items.length > 0) {
            const latestItem = items[items.length - 1];
            singleSelectionItems = [latestItem];
        }
        setSelectedItems(singleSelectionItems);
        if (singleSelectionItems && singleSelectionItems.length > 0) {
            setCurrentRequestId(singleSelectionItems[0].requestId);
            setAccessCodeOrderStatus(singleSelectionItems[0].accessCodeRequestStatus);
            singleSelectionItems[0].accessCodeRequestStatus === 'Completed' ? setDisableActionButtons(false) : setDisableActionButtons(true);
        } else {
            setCurrentRequestId("");
            setDisableActionButtons(true);
        }
    };

    useEffect(() => {
        if (selectedItems && selectedItems.length > 0) {
            setDisableActionButtons(false);
        } else {
            setDisableActionButtons(true);
        }
    }, [selectedItems]);

    useEffect(() => {
        setCurrentPage(0);
        setPageSize(30);
        setActivePageNum(1);
        setSelectedItems([]);
        setCurrentRequestId("");
        setAccessCodeOrderStatus("");
        setApiError("");
        setPaginationResetKey(prev => prev + 1);
        if (!currentIsbn) {
            setManageAccessCodeDetails(null);
        } else {
            setManageAccessCodeDetails(null);
        }
    }, [currentIsbn, setManageAccessCodeDetails]);

    const onPageChange = async (pageIndex) => {
        if (pageIndex === currentPage) {
            return;
        }

        setCurrentPage(pageIndex);
        setActivePageNum(pageIndex + 1);
        setSelectedItems([]);
        setCurrentRequestId("");
        setAccessCodeOrderStatus("");
        setApiError("");
        if (currentIsbn) {
            try {
                await getAccessCodeDetails(currentIsbn, pageIndex + 1, pageSize);
            } catch (error) {
                setApiError("Failed to fetch access code data. Please try again.");
            }
        }
        if (tableRef.current && typeof tableRef.current.focus === 'function') {
            tableRef.current.focus();
        }
    };
    const handlePageSizeChange = async (newPageSize) => {
        if (isLoadingAccessCodes) return;

        const totalElements = manageAccessCodeDetails?.totalElements || 0;

        const actualPageSize = newPageSize === "All" ? totalElements : newPageSize;

        setPageSize(actualPageSize);
        setApiError("");

        const totalPages = Math.ceil(totalElements / actualPageSize);
        const validCurrentPage = currentPage < totalPages ? currentPage : Math.max(0, totalPages - 1);

        if (validCurrentPage !== currentPage) {
            setCurrentPage(validCurrentPage);
            setActivePageNum(validCurrentPage + 1);
        }

        setSelectedItems([]);
        setCurrentRequestId("");
        setAccessCodeOrderStatus("");

        if (currentIsbn) {
            try {
                await getAccessCodeDetails(currentIsbn, validCurrentPage + 1, actualPageSize);
            } catch (error) {
                console.error("Error fetching data with new page size:", error);
                setApiError("Failed to fetch access code data. Please try again.");
            }
        }
    };

     const getCurrentPageData = () => {
        return [...safeAccessCodeDetails].sort((a, b) => {
            return new Date(b.requestDate) - new Date(a.requestDate);
        });
    };

    const columns = useCallback(() => {
        return [
            {
                label: ('Order ID'),
                key: 'orderId',
                preventClick: true,
                render: (id, data) => {
                    const requestData = data.requestId.split(':').pop();
                    return <span>{requestData}</span>
                }
            },
            {
                label: ('Order Date'),
                key: 'requestDate',
                sortable: true,
                preventClick: false,
                render: (id, data) => {
                    const requestDate = data.requestDate.split("T")[0];
                    return <span>{requestDate}</span>
                }
            },
            {
                label: ('Reason'),
                key: 'reason',
                preventClick: true,
                render: (id, data) => {
                    return <span>{data.notes}</span>
                }
            },
            {
                label: ('Redemption Status'),
                key: 'redemption',
                preventClick: true,
                render: (id, data) => {
                    return (<span>
                        <strong>{data.totalNumberOfRedeemedAccessCodes}</strong>/{data.totalNumberOfAccessCodes}
                    </span>
                    )
                }
            },
            {
                label: ('Code Generation Order Status'),
                key: 'orderStatus',
                preventClick: true,
                render: (id, data) => {
                    return (<span className={`access-request-status ${data?.accessCodeRequestStatus?.toLowerCase().replace(/\s+/g, '')}`}>
                        {data.accessCodeRequestStatus}
                    </span>)
                }
            }
        ]
    });
    const additionFooters = () => {
        const pageInfoText = paginationData.pageCount > pageSize ? `${(pageSize * (activePageNum - 1)) + 1}-${perPageDataCount < pageSize ? paginationData.pageCount : activePageNum * pageSize}` : perPageDataCount;
        return (
            <div className="foot-data">{tHTML(`Viewing <strong>%(pageInfoText)s</strong> of <strong>%(assignmentLength)s </strong>`, { unsafe: true, pageInfoText, assignmentLength: (paginationData.pageCount) || 0 })}</div>
        )
    }

    return (
        <>
            {isLoadingAccessCodes && <Loader />}
            <div className="step-two-container">
                <div className='step-two-header'>
                    <div className="access-orders-card">
                        <div className="action-header">
                            <span className="header-icon">2</span>
                            <span className="access-code-heading">Select and download or email codes</span>
                        </div>

                        <div className='card-body'>
                            <div className="access-orders-header">
                                <h4 className="access-orders-heading">Access code orders</h4>

                                {manageAccessCodeDetails && (
                                    <div className="selected-codes-action-wrapper">
                                        {disableActionButtons && (
                                            <div className="tooltip-message">Please select at least one code first</div>
                                        )}

                                        <div className="selected-codes-action">
                                            <Button
                                                className={`${disableActionButtons ? 'disabled' : ''} download-codes-button`}
                                                onClick={() => handleShowActionModal('download')}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/${disableActionButtons ? "download-disabled.svg" : "download-blue.svg"}`}
                                                    alt=""
                                                    className="action-button-icon"
                                                />
                                                <span className="action-button-text">Download selected codes</span>
                                            </Button>

                                            <Button
                                                className={`${disableActionButtons ? 'disabled' : ''} email-codes-button`}
                                                onClick={() => handleShowActionModal('email')}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/${disableActionButtons ? "email-disabled.svg" : "email-blue.svg"}`}
                                                    alt=""
                                                    className="action-button-icon"
                                                />
                                                <span className="action-button-text">Email selected codes</span>
                                            </Button>
                                        </div>
                                    </div>
                                )}

                            </div>
                            {!manageAccessCodeDetails && (
                                <div className='no-access-card-msg'>Access code orders will display here after an ISBN13 has been entered</div>
                            )}
                            {manageAccessCodeDetails && (
                                <div className="access-orders-table">
                                    <div ref={tableRef} tabIndex={-1} className='mb-3'>
                                        <CardWithTable
                                            isMultiselect={true}
                                            columnsData={columns()}
                                            data={getCurrentPageData()}
                                            selectedItems={selectedItems}
                                            setSelectedItems={handleSelectedItemsChange}
                                            renderAdditionalFooter={''}
                                            renderAdditionalHeader={''}
                                            headerClassName="d-flex custom-header"
                                            showCurrentPage={true}
                                            noDataMsg={
                                                apiError
                                                    ? apiError
                                                    : isLoadingAccessCodes
                                                        ? "Loading..."
                                                        : "No access code orders available"
                                            }
                                            noSearchResultMsg="No search results found!"
                                            retry={false}
                                            idKey="requestId"
                                            pageSize={pageSize}
                                            filterColumns={false}
                                            className="remove-table-border task-list"
                                            setPageDataCount={setPerPageDataCount}
                                            setActivePageNum={setActivePageNum}
                                            caption="List of codes"
                                            isPaginationRequired={false}
                                        />
                                    </div>
                                    {(manageAccessCodeDetails?.totalElements > 0) && currentIsbn && (
                                        <div key={`pagination-wrapper-${paginationResetKey} `} className="pagination-wrapper">
                                            <Pagination
                                                key={`pagination-${currentIsbn}-${paginationResetKey}`}
                                                paginationData={paginationData}
                                                tableRef={tableRef}
                                                showPageSizeOptions={true}
                                                pageSizeOptions={[30, 50, 100, 250, "All"]}
                                                bsPrefix="pagination"
                                                activeClass="active"
                                                showCurrentPage={false}
                                                stopScroll={true}
                                                footerDropdown="pagination-dropdown"
                                                footerShowTextCls="pagination-show-text"
                                                footerDropBtnCls="pagination-dropdown-btn"
                                                shortFormat={true}
                                                paginationExtraClass={"manage-access-code-pagination"}
                                            />
                                            <div className="pagination-info">
                                                {totalElements > 0
                                                    ? `Viewing ${startEntry}–${endEntry} of ${totalElements}`
                                                    : `Viewing 0 of 0`}
                                            </div>
                                        </div>
                                    )}

                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {accessCodeModalAction &&
                    <DownloadOrEmailCodesModal
                        actionType={accessCodeModalAction}
                        isOpen={true}
                        onCloseHanlder={() => setAccessCodeModalAction("")}
                        currentRequestId={currentRequestId}
                        accessCodeOrderStatus={accessCodeOrderStatus}
                    />
                }
            </div>
        </>
    );
};

export default ManageAccessCodeStepTwo;
